import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { ConditionEditSheet } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet';
import { ConditionEditSheetTargetFooter } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-target-footer';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import { useConditionAudiencePreview } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-audience-preview';
import { useConditionEditSheet } from '@/components/tree-dnd/use-condition-edit-sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';

interface HasNotificationsEnabledConditionProps {
  node: Extract<TreeConditionNode, { kind: 'hasNotificationsEnabled' }>;
  onChange: (
    value: Extract<
      TreeConditionNode,
      { kind: 'hasNotificationsEnabled' }
    >['args']
  ) => void;
}

export function HasNotificationsEnabledCondition({
  node,
  onChange: _onChange,
}: HasNotificationsEnabledConditionProps) {
  const { LL, locale } = useI18nContext();
  const { open, onOpenChange } = useConditionEditSheet(node.id);
  const conditionLabel =
    LL.audiences.filters.conditions.hasNotificationsEnabled();
  const summaryDetail =
    LL.audiences.filters.conditionSummary.hasNotificationsEnabledSummary();

  const filterSummary = useMemo(() => {
    if (node.negated) {
      return LL.audiences.filters.conditionSummary.hasNotificationsEnabledExcludeSummary();
    }
    return LL.audiences.filters.conditionSummary.hasNotificationsEnabledIncludeSummary();
  }, [LL, node.negated]);

  const preview = useConditionAudiencePreview(node, open);

  return (
    <>
      <ConditionNodeSummary label={conditionLabel} detail={summaryDetail} />
      <ConditionEditSheet
        open={open}
        onOpenChange={onOpenChange}
        title={conditionLabel}
        description={LL.audiences.filters.conditionSummary.hasNotificationsEnabledDescription()}
        footerContent={
          <ConditionEditSheetTargetFooter
            summary={filterSummary}
            targetedUserCount={
              preview.isError ? undefined : preview.targetedUserCount
            }
            totalUserCount={
              preview.isError ? undefined : preview.totalUserCount
            }
            isLoading={preview.isLoading}
            locale={locale as Locales}
          />
        }
      >
        {null}
      </ConditionEditSheet>
    </>
  );
}
